import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
  CircularProgress, Container, IconButton, Alert, Chip, Snackbar, Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteConfirmModal from "../components/AlertComponents/DeleteConfirmModal";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { theme } from "../theme/colors";
import { useUser } from "../context/UserContext";
import {
  createPostReal, updatePost, getPostDetail, deletePost, PostType,
  uploadPostImage, acceptCommunityGuidelines,
} from "../utils/feedApi";
import TOPICS from "../utils/topicLabels";
import CommunityGuidelinesModal from "../components/CommunityComponents/CommunityGuidelinesModal";
import BillSelector from "../components/CommunityComponents/BillSelector";
import EventSelector from "../components/CommunityComponents/EventSelector";
import AudienceSettings from "../components/AudienceSettings";
import PostTypeChipBar from "../components/CommunityComponents/PostTypeChipBar";
import ComposerAttachmentBar from "../components/CommunityComponents/ComposerAttachmentBar";
import AnonymousToggle from "../components/CommunityComponents/AnonymousToggle";
import { getPostPlaceholder, getExternalLinkHelperText, postTypeSupportsExternalLink } from "../utils/postTypeConfig";
import { isPoliticianAccount, canPostAnonymously } from "../utils/accountTypes";
import { ACTION_TYPE_OPTIONS } from "../utils/actionTypeOptions";
import {
  createEmptyPollOptions,
  isPollLockedByVotes,
  postHasPollAttachment,
  derivePollHasVotesFromPost,
  hasMinimumPollOptions,
  POLL_LOCKED_MESSAGE,
  POLL_MAX_OPTIONS,
  POLL_OPTION_MAX_LENGTH,
} from "../utils/pollAttachmentRules";
import {
  buildPostWritePayload,
  clearAttachmentOnToggle,
  POST_CONTENT_MIN_LENGTH,
  POST_CONTENT_MAX_LENGTH,
  validatePostComposer,
} from "../utils/postComposerUtils";
import GooglePlacesAutocomplete, { geocodeByPlaceId } from "react-google-places-autocomplete";

const GOOGLE_PLACES_API_KEY =
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_PUBLIC_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_APP_GOOGLE_CIVIC_API_KEY ||
  "";

const EMPTY_ATTACHMENTS = {
  poll: false, location: false, actionType: false, bill: false,
  event: false, image: false, externalLink: false,
};
const EMPTY_LOCATION = {
  location_name: "", location_city: "", location_state: "", location_address: "",
};
const EMPTY_EDIT_SNAPSHOT = {
  hadPoll: false, pollHasVotes: false, hadLocation: false, hadBill: false,
  hadEvent: false, hadActionType: false, hadImage: false, hadExternalLink: false,
};
const POST_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const POST_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

export default function CommunityCreatePostPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    accountTypeName,
    hasAcceptedCommunityGuidelines, setHasAcceptedCommunityGuidelines, loadUserContext,
    loadingUser,
  } = useUser() || {};

  const isPolitician = isPoliticianAccount(accountTypeName);
  const canChooseAnonymous = canPostAnonymously(accountTypeName);

  const editPostId = location.state?.editPostId || null;
  const isEditMode = !!editPostId;

  // Post type tag (defaults to idea on create)
  const [selectedPostType, setSelectedPostType] = useState(PostType.IDEA);

  // Attachment panel visibility
  const [attachments, setAttachments] = useState({ ...EMPTY_ATTACHMENTS });

  // Form fields
  const [content, setContent] = useState("");
  const [topics, setTopics] = useState([]);
  const [actionType, setActionType] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [geoTarget, setGeoTarget] = useState("everyone");
  const [imageUrl, setImageUrl] = useState("");
  const [externalLinkUrl, setExternalLinkUrl] = useState("");
  const [externalLinkTitle, setExternalLinkTitle] = useState("");
  const [locationFields, setLocationFields] = useState({ ...EMPTY_LOCATION });
  const [locationMode, setLocationMode] = useState(GOOGLE_PLACES_API_KEY ? "search" : "manual");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [context, setContext] = useState(null); // for STORY etc.: { type: 'bill'|'event', id, title, shortLabel }

  // Poll fields
  const [pollOptions, setPollOptions] = useState(createEmptyPollOptions());
  const [allowsMultipleChoices, setAllowsMultipleChoices] = useState(false);

  // Edit-mode snapshot for safe attachment add/remove on save
  const [editSnapshot, setEditSnapshot] = useState({ ...EMPTY_EDIT_SNAPSHOT });
  const [originalStatus, setOriginalStatus] = useState(null);
  const [anonymous, setAnonymous] = useState(false);
  const isAnonymousLocked = isEditMode && originalStatus === "published";

  // UI state
  const [loadingPost, setLoadingPost] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const loadRequestTokenRef = useRef(0);
  const imageRequestTokenRef = useRef(0);

  // Guidelines retry
  const [guidelinesVisible, setGuidelinesVisible] = useState(false);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  const pendingPostDataRef = useRef(null);

  // Delete confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Validation errors
  const [contentError, setContentError] = useState(null);
  const [topicError, setTopicError] = useState(false);
  const [actionTypeError, setActionTypeError] = useState(null);
  const [billError, setBillError] = useState(null);
  const [eventError, setEventError] = useState(null);
  const [externalLinkError, setExternalLinkError] = useState(null);
  const [generalError, setGeneralError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });
  const showToast = (message, severity = "error") => setToast({ open: true, message, severity });

  const resetComposerForm = useCallback(() => {
    imageRequestTokenRef.current += 1;
    setSelectedPostType(PostType.IDEA);
    setAttachments({ ...EMPTY_ATTACHMENTS });
    setContent("");
    setTopics([]);
    setActionType("");
    setVisibility("public");
    setGeoTarget("everyone");
    setAnonymous(false);
    setImageUrl("");
    setExternalLinkUrl("");
    setExternalLinkTitle("");
    setLocationFields({ ...EMPTY_LOCATION });
    setLocationMode(GOOGLE_PLACES_API_KEY ? "search" : "manual");
    setSelectedPlace(null);
    setSelectedBill(null);
    setSelectedEvent(null);
    setContext(null);
    setPollOptions(createEmptyPollOptions());
    setAllowsMultipleChoices(false);
    setEditSnapshot({ ...EMPTY_EDIT_SNAPSHOT });
    setOriginalStatus(null);
    setUploadingImage(false);
    setShowSuccess(false);
    setGuidelinesVisible(false);
    pendingPostDataRef.current = null;
    setShowDeleteConfirm(false);
    setContentError(null);
    setTopicError(false);
    setActionTypeError(null);
    setBillError(null);
    setEventError(null);
    setExternalLinkError(null);
    setGeneralError(null);
  }, []);

  const loadPostData = useCallback(async (postId, requestToken) => {
    try {
      const post = await getPostDetail(postId);
      if (requestToken !== loadRequestTokenRef.current) return;
      setSelectedPostType(post.post_type || PostType.IDEA);
      setContent(post.content || "");
      setTopics(post.topics || []);
      setActionType(post.action_type || "");
      setVisibility(post.visibility || "public");
      setGeoTarget(post.geo_target || "everyone");
      setAnonymous(!!post.anonymous);
      setOriginalStatus(post.status || "published");
      setImageUrl(post.image_url || "");
      setExternalLinkUrl(post.external_link_url || "");
      setExternalLinkTitle(post.external_link_title || "");
      const hasLocation = post.location_name || post.location_city || post.location_state || post.location_address;
      if (hasLocation) {
        setLocationFields({
          location_name: post.location_name || "",
          location_city: post.location_city || "",
          location_state: post.location_state || "",
          location_address: post.location_address || "",
        });
        setLocationMode("manual");
      } else {
        setLocationFields({ ...EMPTY_LOCATION });
        setSelectedPlace(null);
      }
      if (postHasPollAttachment(post)) {
        setPollOptions(post.poll_options.map((o) => o.text));
        setAllowsMultipleChoices(!!post.allows_multiple_choices);
      } else {
        setPollOptions(createEmptyPollOptions());
        setAllowsMultipleChoices(false);
      }
      setSelectedBill(post.bill ? { ...post.bill } : (post.bill_id ? { id: post.bill_id, title: post.bill_title, bill_number: post.bill_number, type: post.bill_type } : null));
      setSelectedEvent(post.event ? { ...post.event } : (post.event_id ? { id: post.event_id, title: post.event_title } : null));
      if (post.bill_id && !post.bill) {
        setContext({ type: "bill", id: post.bill_id, title: post.bill_title || "", shortLabel: post.bill_number || "Bill" });
      } else if (post.event_id && !post.event) {
        setContext({ type: "event", id: post.event_id, title: post.event_title || "", shortLabel: "Event" });
      } else {
        setContext(null);
      }
      setAttachments({
        poll: postHasPollAttachment(post),
        location: !!hasLocation,
        actionType: !!post.action_type,
        bill: !!(post.bill_id || post.bill),
        event: !!(post.event_id || post.event),
        image: !!post.image_url,
        externalLink: !!(post.external_link_url),
      });
      setEditSnapshot({
        hadPoll: postHasPollAttachment(post),
        pollHasVotes: derivePollHasVotesFromPost(post),
        hadLocation: !!hasLocation,
        hadBill: !!(post.bill_id || post.bill),
        hadEvent: !!(post.event_id || post.event),
        hadActionType: !!post.action_type,
        hadImage: !!post.image_url,
        hadExternalLink: !!(post.external_link_url),
      });
    } catch (err) {
      if (requestToken !== loadRequestTokenRef.current) return;
      console.error("Error loading post:", err);
      setGeneralError("Failed to load post.");
      navigate("/community");
    } finally {
      if (requestToken === loadRequestTokenRef.current) setLoadingPost(false);
    }
  }, [navigate]);

  // Load post data when editing
  useEffect(() => {
    const requestToken = ++loadRequestTokenRef.current;
    resetComposerForm();
    if (isEditMode && editPostId) {
      setLoadingPost(true);
      loadPostData(editPostId, requestToken);
    } else {
      setLoadingPost(false);
    }
    return () => {
      if (loadRequestTokenRef.current === requestToken) {
        loadRequestTokenRef.current += 1;
      }
    };
  }, [isEditMode, editPostId, loadPostData, resetComposerForm]);

  useEffect(() => () => {
    imageRequestTokenRef.current += 1;
  }, []);

  // Poll UI follows the attachment toggle only (not the intent tag alone).
  const pollAttachmentActive = attachments.poll;
  // Votes freeze both the poll itself and the tag (backend rejects either change).
  const pollLockedByVotes = isPollLockedByVotes({
    isEditMode,
    pollHasVotes: editSnapshot.pollHasVotes,
  });

  const handleAttachmentToggle = (id, enabled) => {
    if (!enabled && id === "poll" && pollLockedByVotes) {
      setGeneralError(POLL_LOCKED_MESSAGE);
      return;
    }
    if (id === "image" && !enabled) {
      imageRequestTokenRef.current += 1;
      setUploadingImage(false);
    }
    setAttachments((prev) => ({ ...prev, [id]: enabled }));
    if (!enabled) {
      clearAttachmentOnToggle(id, {
        setPollOptions,
        setAllowsMultipleChoices,
        setLocationFields,
        setSelectedPlace,
        setActionType,
        setActionTypeError,
        setSelectedBill,
        setSelectedEvent,
        setContext,
        setImageUrl,
        setExternalLinkUrl,
        setExternalLinkTitle,
        createEmptyPollOptions,
      });
      if (id === "bill") setBillError(null);
      if (id === "event") setEventError(null);
      if (id === "externalLink") setExternalLinkError(null);
    }
  };

  const handlePostTypeChange = (type) => {
    setSelectedPostType(type);
  };

  const supportsGeoTarget = true;
  const showExternalLinkFields =
    attachments.externalLink || postTypeSupportsExternalLink(selectedPostType);
  const charCount = content.trim().length;
  const isUnderMin = charCount > 0 && charCount < POST_CONTENT_MIN_LENGTH;
  const isOverMax = charCount > POST_CONTENT_MAX_LENGTH;

  const parseAddressComponents = useCallback((result) => {
    const components = result?.address_components || [];
    let streetNumber = "", route = "", city = "", state = "", name = "";
    components.forEach((c) => {
      const t = c.types;
      if (t.includes("street_number")) streetNumber = c.long_name;
      else if (t.includes("route")) route = c.long_name;
      else if (t.includes("locality") || t.includes("sublocality") || t.includes("neighborhood")) {
        if (!city) city = c.long_name;
      } else if (t.includes("administrative_area_level_1")) state = c.short_name;
    });
    const address = streetNumber && route ? `${streetNumber} ${route}` : route || streetNumber;
    if (result?.name && result.name !== address) name = result.name;
    return { location_name: name, location_city: city, location_state: state, location_address: address || result?.formatted_address || "" };
  }, []);

  const handlePlaceSelect = useCallback(async (placeOption) => {
    setSelectedPlace(placeOption);
    if (!placeOption?.value?.place_id) {
      setLocationFields((prev) => ({
        ...prev,
        location_city: "",
        location_state: "",
        location_address: "",
      }));
      return;
    }
    try {
      const results = await geocodeByPlaceId(placeOption.value.place_id);
      const parsed = parseAddressComponents(results?.[0]);
      if (!parsed.location_name) parsed.location_name = placeOption.label || "";
      setLocationFields((prev) => ({
        ...prev,
        location_name: prev.location_name || parsed.location_name,
        location_city: parsed.location_city,
        location_state: parsed.location_state,
        location_address: parsed.location_address,
      }));
    } catch {
      setLocationFields((prev) => ({
        ...prev,
        location_name: prev.location_name || placeOption.label || "",
        location_city: "",
        location_state: "",
        location_address: "",
      }));
    }
  }, [parseAddressComponents]);

  const clearErrors = () => {
    setContentError(null);
    setTopicError(false);
    setActionTypeError(null);
    setBillError(null);
    setEventError(null);
    setExternalLinkError(null);
    setGeneralError(null);
  };

  const validate = (isDraft = false) => {
    clearErrors();
    const billId = selectedBill?.id ?? (context?.type === "bill" ? context.id : null);
    const eventId = selectedEvent?.id ?? (context?.type === "event" ? context.id : null);
    const result = validatePostComposer({
      content,
      isDraft,
      topics,
      attachments,
      actionType,
      pollAttachmentActive,
      pollOptions,
      billId,
      eventId,
      externalLinkUrl,
    });
    setContentError(result.errors.content || null);
    setTopicError(Boolean(result.errors.topics));
    setActionTypeError(result.errors.actionType || null);
    setBillError(result.errors.bill || null);
    setEventError(result.errors.event || null);
    setExternalLinkError(result.errors.externalLink || null);
    setGeneralError(result.errors.general || null);
    return result.ok;
  };

  const buildPostData = (status) => {
    const billId = selectedBill?.id ?? (context?.type === "bill" ? context.id : null);
    const eventId = selectedEvent?.id ?? (context?.type === "event" ? context.id : null);

    return buildPostWritePayload({
      anonymous: canChooseAnonymous && !isAnonymousLocked ? anonymous : undefined,
      status,
      isEditMode,
      editSnapshot,
      attachments,
      pollAttachmentActive,
      supportsGeoTarget,
      selectedPostType,
      content,
      topics,
      visibility,
      geoTarget,
      imageUrl,
      actionType,
      pollOptions,
      allowsMultipleChoices,
      locationFields,
      billId,
      eventId,
      externalLinkUrl,
      externalLinkTitle,
    });
  };

  const submitPost = async (postData, target = { isEditMode, editPostId }) => {
    if (target.isEditMode) {
      await updatePost(target.editPostId, postData);
    } else {
      await createPostReal(postData);
    }
  };

  const submitDisabled =
    submitting
    || uploadingImage
    || loadingUser
    || !content.trim()
    || isUnderMin
    || isOverMax
    || (attachments.actionType && !actionType)
    || (pollAttachmentActive && !hasMinimumPollOptions(pollOptions));

  const getPlaceholder = () => getPostPlaceholder(selectedPostType);

  const handleSubmit = async () => {
    if (!validate(false)) return;
    const postData = buildPostData("published");
    const target = { isEditMode, editPostId };
    if (!isEditMode && !loadingUser && hasAcceptedCommunityGuidelines === false) {
      pendingPostDataRef.current = { postData, target };
      setGuidelinesVisible(true);
      return;
    }
    setSubmitting(true);
    setGeneralError(null);
    try {
      await submitPost(postData, target);
      setShowSuccess(true);
    } catch (error) {
      if (error.response?.data?.code === "GUIDELINES_NOT_ACCEPTED") {
        pendingPostDataRef.current = { postData, target };
        setGuidelinesVisible(true);
      } else {
        setGeneralError(error.response?.data?.error || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!validate(true)) return;
    const postData = buildPostData("draft");
    const target = { isEditMode, editPostId };
    if (!isEditMode && !loadingUser && hasAcceptedCommunityGuidelines === false) {
      pendingPostDataRef.current = { postData, target };
      setGuidelinesVisible(true);
      return;
    }
    setSubmitting(true);
    setGeneralError(null);
    try {
      await submitPost(postData, target);
      navigate("/dashboard", { state: { draftSaved: true } });
    } catch (error) {
      if (error.response?.data?.code === "GUIDELINES_NOT_ACCEPTED") {
        pendingPostDataRef.current = { postData, target };
        setGuidelinesVisible(true);
      } else {
        setGeneralError(error.response?.data?.error || "Failed to save draft. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!isEditMode || !editPostId || uploadingImage) return;
    setShowDeleteConfirm(false);
    try {
      setSubmitting(true);
      await deletePost(editPostId);
      navigate("/community");
    } catch (err) {
      setGeneralError(err.response?.data?.error || "Failed to delete post.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptGuidelines = async () => {
    setGuidelinesLoading(true);
    try {
      await acceptCommunityGuidelines();
      if (setHasAcceptedCommunityGuidelines) setHasAcceptedCommunityGuidelines(true);
      if (loadUserContext) await loadUserContext();
      setGuidelinesVisible(false);

      if (pendingPostDataRef.current) {
        const pending = pendingPostDataRef.current;
        const wasDraft = pending.postData.status === "draft";
        setSubmitting(true);
        try {
          await submitPost(pending.postData, pending.target);
          pendingPostDataRef.current = null;
          if (wasDraft) {
            navigate("/dashboard", { state: { draftSaved: true } });
          } else {
            setShowSuccess(true);
          }
        } catch (retryErr) {
          setGeneralError(retryErr.response?.data?.error || "Something went wrong. Please try again.");
        } finally {
          setSubmitting(false);
        }
      }
    } catch (err) {
      console.error("Error accepting guidelines:", err);
      showToast("We couldn't save your guidelines acceptance. Check your connection and try again.", "error");
    } finally {
      setGuidelinesLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!POST_IMAGE_TYPES.has(file.type)) {
      setGeneralError("Choose a JPEG, PNG, WebP, or GIF image.");
      return;
    }
    if (file.size > POST_IMAGE_MAX_BYTES) {
      setGeneralError("Image is too large. Choose an image up to 10 MB.");
      return;
    }
    const requestToken = ++imageRequestTokenRef.current;
    try {
      setUploadingImage(true);
      setGeneralError(null);
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error("Image read failed"));
        reader.readAsDataURL(file);
      });
      if (requestToken !== imageRequestTokenRef.current) return;
      const response = await uploadPostImage(base64Data, file.type);
      if (requestToken !== imageRequestTokenRef.current) return;
      setImageUrl(response.image_url);
      setAttachments((prev) => ({ ...prev, image: true }));
    } catch (err) {
      if (requestToken !== imageRequestTokenRef.current) return;
      console.error("Image upload failed", err);
      setGeneralError("Failed to upload image.");
    } finally {
      if (requestToken === imageRequestTokenRef.current) {
        setUploadingImage(false);
      }
    }
  };

  const handleRemoveImage = () => {
    imageRequestTokenRef.current += 1;
    setUploadingImage(false);
    setImageUrl("");
    setAttachments((prev) => ({ ...prev, image: false }));
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    if (isEditMode) navigate(`/community/post/${editPostId}`);
    else navigate("/community");
  };

  // --- Loading state ---
  if (loadingPost) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: theme.primary }} />
        <Typography sx={{ ml: 2, color: "#64748b" }}>Loading post...</Typography>
      </Box>
    );
  }

  // --- Success state ---
  if (showSuccess) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ textAlign: "center", p: 4, maxWidth: 400 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: theme.primary, mb: 2 }} />
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#0f172a", mb: 1 }}>
            {isEditMode && originalStatus === "draft"
              ? "Draft Published!"
              : isEditMode
                ? "Post Updated!"
                : "Post Published!"}
          </Typography>
          <Typography sx={{ fontSize: 15, color: "#64748b", mb: 3 }}>
            Your post is now live in the community feed.
          </Typography>
          <Button
            variant="contained"
            onClick={handleSuccessClose}
            sx={{
              background: theme.gradient, color: "#fff", fontWeight: 700, fontSize: 16,
              textTransform: "none", borderRadius: "12px", px: 4, py: 1.5,
              "&:hover": { background: theme.gradient, opacity: 0.92 },
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    );
  }

  // --- Main form ---
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: 660 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
            {isEditMode ? (originalStatus === "draft" ? "Edit Draft" : "Edit Post") : "Create Post"}
          </Typography>
          <Button
            onClick={() => navigate(-1)}
            sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}
          >
            Cancel
          </Button>
        </Box>

        <PostTypeChipBar
          value={selectedPostType}
          onChange={handlePostTypeChange}
          disabled={pollLockedByVotes}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Content */}
          <Box>
            {pollAttachmentActive && (
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: 1 }}>
                Poll Question <span style={{ color: "#ef4444", fontSize: "1.25em", fontWeight: 600 }}>*</span>
              </Typography>
            )}
            <TextField
              fullWidth multiline minRows={7}
              placeholder={getPlaceholder()}
              value={content}
              onChange={(e) => { setContent(e.target.value); setContentError(null); }}
              aria-label={pollAttachmentActive ? "Poll question" : "Your post"}
              required
              sx={{ 
                "& .MuiOutlinedInput-root": { 
                  borderRadius: "12px", 
                  fontSize: 16, 
                  bgcolor: "#fff",
                  "& fieldset": { borderColor: contentError || isOverMax ? "#ef4444" : (!isUnderMin && !isOverMax && charCount > 0 ? `${theme.primary}50` : "#e5e7eb"), borderWidth: "2px" },
                  "&:hover fieldset": { borderColor: contentError || isOverMax ? "#ef4444" : (!isUnderMin && !isOverMax && charCount > 0 ? `${theme.primary}50` : "#d1d5db") },
                  "&.Mui-focused fieldset": { borderColor: contentError || isOverMax ? "#ef4444" : theme.primary },
                } 
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1.25 }}>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.75,
                  py: 0.85,
                  borderRadius: "9999px",
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.01em",
                  ...(charCount >= POST_CONTENT_MIN_LENGTH
                    ? { bgcolor: `${theme.primary}1a`, color: "#065f46", border: `1px solid ${theme.primary}55` }
                    : charCount > 0
                      ? { bgcolor: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }
                      : { bgcolor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }),
                }}
              >
                Min {charCount} / {POST_CONTENT_MIN_LENGTH}
              </Box>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.75,
                  py: 0.85,
                  borderRadius: "9999px",
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.01em",
                  ...(isOverMax
                    ? { bgcolor: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }
                    : POST_CONTENT_MAX_LENGTH - charCount <= 0
                      ? { bgcolor: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }
                      : POST_CONTENT_MAX_LENGTH - charCount <= 150
                        ? { bgcolor: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }
                        : { bgcolor: `${theme.primary}14`, color: "#065f46", border: `1px solid ${theme.primary}45` }),
                }}
              >
                {Math.max(0, POST_CONTENT_MAX_LENGTH - charCount)} left
              </Box>
            </Box>
            {contentError && (
              <Typography sx={{ fontSize: 13, color: "#ef4444", mt: 1, pl: 0.5 }}>{contentError}</Typography>
            )}
          </Box>

          <ComposerAttachmentBar
            active={attachments}
            onToggle={handleAttachmentToggle}
            lockedIds={pollLockedByVotes ? ["poll"] : []}
          />

          {/* Poll options */}
          {pollAttachmentActive && (
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: 1.5 }}>
                Poll Options <span style={{ color: "#ef4444", fontSize: "1.25em", fontWeight: 600 }}>*</span>
              </Typography>
              {pollOptions.map((opt, idx) => (
                <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Option ${idx + 1}`}
                    value={opt}
                    onChange={(e) => {
                      const next = [...pollOptions];
                      next[idx] = e.target.value.slice(0, POLL_OPTION_MAX_LENGTH);
                      setPollOptions(next);
                    }}
                    disabled={pollLockedByVotes}
                    inputProps={{ maxLength: POLL_OPTION_MAX_LENGTH }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px", fontSize: 15, bgcolor: "#fff",
                        "& fieldset": { borderColor: "#e5e7eb", borderWidth: "2px" },
                        "&:hover fieldset": { borderColor: "#d1d5db" },
                        "&.Mui-focused fieldset": { borderColor: theme.primary },
                      },
                    }}
                  />
                  {pollOptions.length > 2 && !pollLockedByVotes && (
                    <IconButton
                      size="small"
                      onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== idx))}
                      sx={{ color: "#94a3b8" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
              {pollOptions.length < POLL_MAX_OPTIONS && !pollLockedByVotes && (
                <Button
                  size="small"
                  onClick={() => setPollOptions([...pollOptions, ""])}
                  sx={{
                    textTransform: "none",
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: "8px",
                    mt: 0.5,
                    color: theme.primary,
                    backgroundColor: "#ffffff",
                    border: `1.5px solid ${theme.primary}`,
                    boxShadow: "none",
                    minHeight: "auto",
                    py: 0.75,
                    px: 1.5,
                    "&:hover": {
                      backgroundColor: `${theme.primary}14`,
                      color: theme.primaryHover,
                      borderColor: theme.primaryHover,
                      boxShadow: "none",
                    },
                  }}
                >
                  + Add option
                </Button>
              )}
              {!pollLockedByVotes && (
                <FormControlLabel
                  sx={{
                    mt: 2, mx: 0, px: 1, py: 0.5, width: "100%",
                    borderRadius: "10px",
                    bgcolor: allowsMultipleChoices ? `${theme.primary}10` : "#f8fafc",
                    border: `1.5px solid ${allowsMultipleChoices ? theme.primary : "#e5e7eb"}`,
                  }}
                  control={
                    <Checkbox
                      checked={allowsMultipleChoices}
                      onChange={(e) => setAllowsMultipleChoices(e.target.checked)}
                      sx={{ color: "#475569", "&.Mui-checked": { color: theme.primary } }}
                    />
                  }
                  label="Allow voters to select multiple choices"
                />
              )}
            </Box>
          )}

          {/* Add image */}
          {attachments.image && (
          <Box>
            {imageUrl ? (
              <Box sx={{ position: "relative", mb: 1.5 }}>
                <img src={imageUrl} alt="Post attachment" style={{ width: "100%", maxHeight: 250, objectFit: "cover", borderRadius: 12 }} />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  aria-label="Remove image"
                  sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(255,255,255,0.85)", "&:hover": { bgcolor: "#fff" } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  p: 2,
                  cursor: uploadingImage ? "default" : "pointer",
                  opacity: uploadingImage ? 0.7 : 1,
                  "&:hover": { borderColor: uploadingImage ? "#e5e7eb" : "#d1d5db" }
                }}
              >
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                />
                {uploadingImage ? (
                  <CircularProgress size={24} sx={{ color: theme.primary }} />
                ) : (
                  <PhotoCameraOutlinedIcon sx={{ fontSize: 24, color: theme.primary }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0f172a", mb: 0.5 }}>
                    {uploadingImage ? "Uploading..." : "Add image from your device"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#0f172a", lineHeight: 1.4 }}>
                    Images display best in a 16:9 landscape format. Your file will not be cropped.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          )}

          {/* What I did */}
          {attachments.actionType && (
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: 1 }}>
                What I did and encourage others to share their tasks{" "}
                <span style={{ color: "#ef4444", fontSize: "1.25em", fontWeight: 600 }}>*</span>
              </Typography>
              <FormControl fullWidth size="medium" error={!!actionTypeError}>
                <Select
                  value={actionType}
                  displayEmpty
                  onChange={(e) => { setActionType(e.target.value); setActionTypeError(null); }}
                  sx={{ borderRadius: "12px", bgcolor: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" } }}
                >
                  <MenuItem value="" disabled>Select what you did...</MenuItem>
                  {ACTION_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                {actionTypeError && <Typography sx={{ fontSize: 13, color: "#ef4444", mt: 1, pl: 0.5 }}>{actionTypeError}</Typography>}
              </FormControl>
            </Box>
          )}

          {attachments.location && (
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: 1 }}>
                Location
              </Typography>

              <TextField
                fullWidth
                label="Venue or place name"
                placeholder="e.g. City Hall, Community Center"
                value={locationFields.location_name}
                onChange={(e) => setLocationFields((prev) => ({ ...prev, location_name: e.target.value }))}
                helperText="Optional — add if the venue has a name."
                FormHelperTextProps={{ sx: { color: "#64748b", mx: 0, mt: 0.75 } }}
                sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" } }}
              />

              {GOOGLE_PLACES_API_KEY && (
                <Box
                  role="group"
                  aria-label="How to enter location"
                  sx={{
                    display: "flex",
                    p: "3px",
                    borderRadius: "10px",
                    bgcolor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    gap: "3px",
                    mb: 1.25,
                    minHeight: 44,
                  }}
                >
                  {[
                    { id: "search", label: "Search" },
                    { id: "manual", label: "Enter manually" },
                  ].map(({ id, label }) => {
                    const active = locationMode === id;
                    return (
                      <Button
                        key={id}
                        disableElevation
                        onClick={() => setLocationMode(id)}
                        sx={{
                          flex: 1,
                          py: 0.75,
                          px: 1.25,
                          minHeight: 38,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 13,
                          borderRadius: "8px",
                          boxShadow: active ? "0 1px 3px rgba(15, 23, 42, 0.08)" : "none",
                          bgcolor: active ? "#ffffff" : "transparent",
                          color: active ? theme.primary : "#64748b",
                          border: active ? `1px solid ${theme.primary}33` : "1px solid transparent",
                          "&:hover": {
                            bgcolor: active ? "#ffffff" : "rgba(255,255,255,0.6)",
                            color: active ? theme.primary : "#334155",
                          },
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Box>
              )}

              {locationMode === "search" && GOOGLE_PLACES_API_KEY ? (
                <Box>
                  <GooglePlacesAutocomplete
                    apiKey={GOOGLE_PLACES_API_KEY}
                    autocompletionRequest={{ componentRestrictions: { country: "us" } }}
                    debounce={300}
                    selectProps={{
                      value: selectedPlace,
                      onChange: handlePlaceSelect,
                      placeholder: "Start typing an address or place name…",
                      isClearable: true,
                      styles: {
                        control: (provided, state) => ({
                          ...provided,
                          minHeight: 48,
                          borderRadius: 12,
                          borderColor: state.isFocused ? theme.primary : "#e5e7eb",
                          boxShadow: state.isFocused ? `0 0 0 1px ${theme.primary}` : "none",
                          "&:hover": { borderColor: state.isFocused ? theme.primary : "#d1d5db" },
                        }),
                        placeholder: (provided) => ({ ...provided, color: "#9ca3af" }),
                        menu: (provided) => ({ ...provided, zIndex: 60, borderRadius: 12, overflow: "hidden" }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isFocused ? "#f8fafc" : "#fff",
                          color: "#111827",
                          paddingTop: 10,
                          paddingBottom: 10,
                        }),
                      },
                    }}
                  />
                  {(locationFields.location_address || locationFields.location_city || locationFields.location_state) && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.25,
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          bgcolor: `${theme.primary}14`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <LocationOnOutlinedIcon sx={{ fontSize: 18, color: theme.primary }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 14, color: "#334155", lineHeight: 1.45 }}>
                          {[locationFields.location_address, locationFields.location_city, locationFields.location_state].filter(Boolean).join(", ")}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        aria-label="Clear selected address"
                        onClick={() => {
                          setLocationFields((prev) => ({
                            ...prev,
                            location_city: "",
                            location_state: "",
                            location_address: "",
                          }));
                          setSelectedPlace(null);
                        }}
                        sx={{ mt: -0.25, mr: -0.25 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <TextField
                      fullWidth
                      label="Street address"
                      placeholder="123 Main Street"
                      value={locationFields.location_address}
                      onChange={(e) => setLocationFields((prev) => ({ ...prev, location_address: e.target.value }))}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" } }}
                    />
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <TextField
                        fullWidth
                        label="City"
                        placeholder="City"
                        value={locationFields.location_city}
                        onChange={(e) => setLocationFields((prev) => ({ ...prev, location_city: e.target.value }))}
                        sx={{ flex: "1 1 160px", minWidth: 140, "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" } }}
                      />
                      <TextField
                        label="State"
                        placeholder="NY"
                        value={locationFields.location_state}
                        onChange={(e) => setLocationFields((prev) => ({ ...prev, location_state: e.target.value.toUpperCase().slice(0, 2) }))}
                        sx={{ flex: "0 0 96px", width: 96, "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" } }}
                        inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
                      />
                    </Box>
                </Box>
              )}
            </Box>
          )}

          {attachments.bill && (
            <BillSelector
              selectedBill={selectedBill}
              onBillSelect={(bill) => { setSelectedBill(bill); setBillError(null); }}
              onClearBill={() => { setSelectedBill(null); setBillError(null); }}
              label="Link to a Bill"
              error={billError}
            />
          )}

          {attachments.event && (
            <EventSelector
              selectedEvent={selectedEvent}
              onEventSelect={(event) => { setSelectedEvent(event); setEventError(null); }}
              label="Link to an Event"
              includePast
              error={eventError}
            />
          )}

          {/* External link */}
          {showExternalLinkFields && (
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: 1 }}>
                Add a Link
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#64748b", mb: 1.5 }}>
                {getExternalLinkHelperText(selectedPostType)}
              </Typography>
              <TextField
                fullWidth
                placeholder="https://..."
                value={externalLinkUrl}
                onChange={(e) => { setExternalLinkUrl(e.target.value); setExternalLinkError(null); }}
                error={!!externalLinkError}
                helperText={externalLinkError}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" }, mb: 1.5 }}
              />
              {externalLinkUrl && (
                <TextField
                  fullWidth
                  placeholder="Link title"
                  value={externalLinkTitle}
                  onChange={(e) => setExternalLinkTitle(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" } }}
                />
              )}
            </Box>
          )}

          {/* Topics */}
          <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: 1 }}>
                Topics
              </Typography>
              <FormControl fullWidth size="medium" error={!!topicError}>
                <Select
                  multiple
                  value={topics}
                  onChange={(e) => {
                    const next = Array.isArray(e.target.value) ? e.target.value : [];
                    if (next.length <= 3) {
                      setTopics(next);
                    }
                    setTopicError(false);
                  }}
                  displayEmpty
                  IconComponent={(props) => <ExpandMoreIcon {...props} sx={{ fontSize: 22, color: "#475569" }} />}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) return "Select topics...";
                    return (
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, py: 0 }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {selected.map((id) => {
                          const label = TOPICS.find((t) => t.id === id)?.label || id;
                          const idToRemove = id;
                          return (
                            <Chip
                              key={id}
                              label={label}
                              size="small"
                              onDelete={(e) => {
                                if (e) { e.stopPropagation(); e.preventDefault(); }
                                const toRemove = idToRemove;
                                setTopicError(false);
                                setTimeout(() => {
                                  setTopics((prev) => prev.filter((x) => String(x) !== String(toRemove)));
                                }, 0);
                              }}
                              sx={{
                                height: 28,
                                fontSize: 14,
                                fontWeight: 500,
                                bgcolor: `${theme.primary}15`,
                                color: "#0f172a",
                                border: `1px solid ${theme.primary}40`,
                                "& .MuiChip-deleteIcon": { fontSize: 16, color: "#64748b", "&:hover": { color: "#0f172a" } },
                              }}
                            />
                          );
                        })}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    disablePortal: false,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: {
                      sx: {
                        maxHeight: 280,
                        mt: 1.5,
                        borderRadius: "12px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                        border: "1px solid #e5e7eb",
                      },
                    },
                  }}
                  sx={{
                    borderRadius: "12px",
                    bgcolor: "#fff",
                    fontWeight: 500,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                    "& .MuiSelect-select": { py: 0.75, minHeight: "auto", pr: "40px !important" },
                    "& .MuiSelect-icon": { right: 14 },
                  }}
                >
                  {TOPICS.map((topic) => (
                    <MenuItem 
                      key={topic.id} 
                      value={topic.id}
                      disabled={topics.length >= 3 && !topics.includes(topic.id)}
                    >
                      {topic.label}
                    </MenuItem>
                  ))}
                </Select>
                {topicError && (
                  <Typography sx={{ fontSize: 13, color: "#ef4444", mt: 1, pl: 0.5 }}>
                    You can select up to 3 topics.
                  </Typography>
                )}
              </FormControl>
            </Box>

          <AudienceSettings
            visibility={visibility}
            onVisibilityChange={setVisibility}
            geoTarget={geoTarget}
            onGeoTargetChange={setGeoTarget}
            showGeoTarget={supportsGeoTarget}
            contentLabel="post"
          />

          {/* General error */}
          {generalError && (
            <Alert severity="error" onClose={() => setGeneralError(null)} sx={{ borderRadius: "10px" }}>
              {generalError}
            </Alert>
          )}

        </Box>

        {/* Action buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", mt: 0.25, gap: 0.75, pb: 4 }}>
          {canChooseAnonymous && (
            <AnonymousToggle
              checked={anonymous}
              onChange={setAnonymous}
              disabled={submitting || isAnonymousLocked}
              helperText={isAnonymousLocked ? "Name visibility can’t be changed after publishing." : undefined}
            />
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitDisabled}
            sx={{
              fontWeight: 700, textTransform: "none", borderRadius: "12px", fontSize: 16,
              width: "100%", minHeight: 52, py: 1.5,
              background: theme.gradient, color: "#fff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              "&:hover": { background: theme.gradient, opacity: 0.92 },
              "&:disabled": { opacity: 0.5, color: "#fff", background: theme.primary }
            }}
          >
            {submitting ? (
              <CircularProgress size={22} color="inherit" />
            ) : isEditMode && originalStatus === "draft" ? (
              "Publish Draft"
            ) : isEditMode ? (
              "Update Post"
            ) : (
              "Publish Post"
            )}
          </Button>

          {(!isEditMode || originalStatus === "draft") && (
            <Button
              onClick={handleSaveDraft}
              disabled={submitting || uploadingImage || loadingUser || !content.trim()}
              startIcon={<SaveOutlinedIcon />}
              sx={{ 
                textTransform: "none", color: "#0f172a", fontWeight: 600, borderRadius: "12px", 
                fontSize: 16, width: "100%", minHeight: 52, py: 1.5, border: "1px solid #d1d5db", bgcolor: "#fff",
                "&:hover": { bgcolor: "#f9fafb" },
                "&:disabled": { opacity: 0.5 }
              }}
            >
              Save Draft
            </Button>
          )}

          {isEditMode && (
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={submitting || uploadingImage}
              startIcon={<DeleteOutlineIcon />}
              sx={{ 
                textTransform: "none", color: "#ef4444", fontWeight: 600, borderRadius: "12px", 
                fontSize: 15, width: "100%", py: 1.5, border: "1px solid #ef4444", bgcolor: "#fff",
                "&:hover": { bgcolor: "#fef2f2" },
                "&:disabled": { opacity: 0.5 }
              }}
            >
              Delete Post
            </Button>
          )}
        </Box>
      </Container>

      {/* Guidelines modal (backend fallback) */}
      <CommunityGuidelinesModal
        open={guidelinesVisible}
        onClose={() => { setGuidelinesVisible(false); pendingPostDataRef.current = null; }}
        onAccept={handleAcceptGuidelines}
        loading={guidelinesLoading}
        isPolitician={isPolitician}
      />

      <DeleteConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeletePost}
      />
      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setToast(t => ({ ...t, open: false }))} severity={toast.severity} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
